import { newTracker, trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import Cookies from 'js-cookie';
import { COOKIE_FOR_MOBILE_NAME, COOKIE_FOR_SESSION_NAME, EXPONEA, GB_LOCALES, LOCAL_ENVS, SNOWPLOW, AUTH_TOKEN_KEY, AUTH_TIME_DIFF_KEY, US_LOCALES, } from './constants';
export const isCSR = () => typeof window !== 'undefined';
export const isCOM = () => { var _a; return isCSR() && ((_a = window.location.hostname) === null || _a === void 0 ? void 0 : _a.endsWith('.com')); };
export const isRaisinFrontend = () => { var _a; return isCSR() && ((_a = window.location.pathname) === null || _a === void 0 ? void 0 : _a.includes('savingglobal')); };
const detectMobileBrowser = () => {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(navigator.userAgent)) {
        return 'web-mobile';
    }
    else {
        return 'web';
    }
};
const getCurrentPlatform = () => {
    const { pathname, host } = location;
    const locale = pathname === null || pathname === void 0 ? void 0 : pathname.split('/')[1].toLowerCase();
    return isCOM() ? `${host}/${locale}` : host;
};
export const getBrowsingApp = () => Cookies.get(COOKIE_FOR_MOBILE_NAME) ? 'app-webview' : detectMobileBrowser();
export const getCountryPlatform = () => getCurrentPlatform().replace('www.', '');
export const isLocalEnv = LOCAL_ENVS.some((env) => isCSR() && env.test(window.location.host));
export const hasCookieForSession = () => {
    const cookie = Cookies.get(COOKIE_FOR_SESSION_NAME);
    return !!cookie && cookie === 'true';
};
export const initSnowplow = (locale, defaultAppId) => {
    var _a;
    const snowplowAppId = Cookies.get(COOKIE_FOR_MOBILE_NAME) === 'true'
        ? 'mobile_app'
        : SNOWPLOW.APP_IDS[getCurrentPlatform()];
    const appId = defaultAppId || snowplowAppId || SNOWPLOW.APP_IDS.default;
    newTracker(SNOWPLOW.TRACKER_ID, isLocalEnv ? SNOWPLOW.TEST_ENDPOINT : SNOWPLOW.PROD_ENDPOINT, {
        appId,
        discoverRootDomain: false,
        cookieDomain: US_LOCALES.includes(locale.toLowerCase())
            ? '.raisin.com'
            : window.location.hostname.replace('www', ''),
    });
    const snowplowUnsentEvent = (_a = window === null || window === void 0 ? void 0 : window.localStorage) === null || _a === void 0 ? void 0 : _a.getItem(`snowplowOutQueue_${SNOWPLOW.TRACKER_ID}_post2`);
    // if there is an unsent event (which is stored in local storage)
    // send an empty event to trigger the unsent event
    if (snowplowUnsentEvent && JSON.parse(snowplowUnsentEvent).length > 0) {
        trackSelfDescribingEvent({
            event: {
                schema: SNOWPLOW.SCHEMA,
                data: {},
            },
        });
    }
};
export const initExponea = (locale) => {
    if (window === null || window === void 0 ? void 0 : window['exponea']) {
        return;
    }
    let exponeaToken;
    if (GB_LOCALES.includes(locale)) {
        exponeaToken = isLocalEnv ? EXPONEA.TEST_TOKEN.GB : EXPONEA.PROD_TOKEN.GB;
    }
    else if (US_LOCALES.includes(locale.toLowerCase())) {
        exponeaToken = isLocalEnv ? EXPONEA.TEST_TOKEN.US : EXPONEA.PROD_TOKEN.US;
    }
    else {
        exponeaToken = isLocalEnv ? EXPONEA.TEST_TOKEN.EU : EXPONEA.PROD_TOKEN.EU;
    }
    window.addEventListener('ucEvent', function (event) {
        createAndInjectExponeaSnippet(exponeaToken, locale, 
        // @ts-ignore
        event.detail[EXPONEA.USER_CENTRICS_CONSENT_NAME]);
    });
};
const createAndInjectExponeaSnippet = (token, locale, isConsentGiven = false) => {
    const exponeaSnippet = document.createElement('script');
    exponeaSnippet.setAttribute('type', 'text/javascript');
    exponeaSnippet.setAttribute('data-usercentrics', 'Exponea');
    let trackConfig = '';
    if (isConsentGiven) {
        trackConfig = `track: {
      visits: true,
    },
    spa_reloading: {
      on_hash_change: false,
      on_url_change: true,
      visits: true,
    },`;
    }
    // If consent is given, we allow personalized web layers, otherwise we don't.
    const personalizedConfig = isConsentGiven ? 'personalized_weblayers: true,' : '';
    const target = US_LOCALES.includes(locale.toLowerCase())
        ? 'https://api.us1.exponea.com'
        : 'https://raisin-api.exponea.com';
    const consentWarning = isConsentGiven
        ? 'exponea.start();'
        : 'console.warn("Exponea Weblayer blocked due to missing consent");';
    exponeaSnippet.text = `!function(e,n,t,i,r,o){function s(e){if("number"!=typeof e)return e;var n=new Date;return new Date(n.getTime()+1e3*e)}var a=4e3,c="xnpe_async_hide";function p(e){return e.reduce((function(e,n){return e[n]=function(){e._.push([n.toString(),arguments])},e}),{_:[]})}function m(e,n,t){var i=t.createElement(n);i.src=e;var r=t.getElementsByTagName(n)[0];return r.parentNode.insertBefore(i,r),i}function u(e){return"[object Date]"===Object.prototype.toString.call(e)}o.target=o.target||"https://api.exponea.com",o.file_path=o.file_path||o.target+"/js/exponea.min.js",r[n]=p(["anonymize","initialize","identify","getSegments","update","track","trackLink","trackEnhancedEcommerce","getHtml","showHtml","showBanner","showWebLayer","ping","getAbTest","loadDependency","getRecommendation","reloadWebLayers","_preInitialize","_initializeConfig"]),r[n].notifications=p(["isAvailable","isSubscribed","subscribe","unsubscribe"]),r[n].segments=p(["subscribe"]),r[n]["snippetVersion"]="v2.7.0",function(e,n,t){e[n]["_"+t]={},e[n]["_"+t].nowFn=Date.now,e[n]["_"+t].snippetStartTime=e[n]["_"+t].nowFn()}(r,n,"performance"),function(e,n,t,i,r,o){e[r]={sdk:e[i],sdkObjectName:i,skipExperiments:!!t.new_experiments,sign:t.token+"/"+(o.exec(n.cookie)||["","new"])[1],path:t.target}}(r,e,o,n,i,RegExp("__exponea_etc__"+"=([\\w-]+)")),function(e,n,t){m(e.file_path,n,t)}(o,t,e),function(e,n,t,i,r,o,p){if(e.new_experiments){!0===e.new_experiments&&(e.new_experiments={});var l,f=e.new_experiments.hide_class||c,_=e.new_experiments.timeout||a,g=encodeURIComponent(o.location.href.split("#")[0]);e.cookies&&e.cookies.expires&&("number"==typeof e.cookies.expires||u(e.cookies.expires)?l=s(e.cookies.expires):e.cookies.expires.tracking&&("number"==typeof e.cookies.expires.tracking||u(e.cookies.expires.tracking))&&(l=s(e.cookies.expires.tracking))),l&&l<new Date&&(l=void 0);var d=e.target+"/webxp/"+n+"/"+o[t].sign+"/modifications.min.js?http-referer="+g+"&timeout="+_+"ms"+(l?"&cookie-expires="+Math.floor(l.getTime()/1e3):"");"sync"===e.new_experiments.mode&&o.localStorage.getItem("__exponea__sync_modifications__")?function(e,n,t,i,r){t[r][n]="<"+n+' src="'+e+'"></'+n+">",i.writeln(t[r][n]),i.writeln("<"+n+">!"+r+".init && document.writeln("+r+"."+n+'.replace("/'+n+'/", "/'+n+'-async/").replace("><", " async><"))</'+n+">")}(d,n,o,p,t):function(e,n,t,i,r,o,s,a){o.documentElement.classList.add(e);var c=m(t,i,o);function p(){r[a].init||m(t.replace("/"+i+"/","/"+i+"-async/"),i,o)}function u(){o.documentElement.classList.remove(e)}c.onload=p,c.onerror=p,r.setTimeout(u,n),r[s]._revealPage=u}(f,_,d,n,o,p,r,t)}}(o,t,i,0,n,r,e),function(e,n,t){var i;e[n]._initializeConfig(t),(null===(i=t.experimental)||void 0===i?void 0:i.non_personalized_weblayers)&&e[n]._preInitialize(t),e[n].start=function(i){i&&Object.keys(i).forEach((function(e){return t[e]=i[e]})),e[n].initialize(t)}}(r,n,o)}(document,"exponea","script","webxpClient",window,{
    target: "${target}",
    token: "${token}",
    ${trackConfig}
    ${personalizedConfig}
    experimental: {
        non_personalized_weblayers: true
    }
  });
  ${consentWarning}
  `;
    document.getElementsByTagName('head')[0].appendChild(exponeaSnippet);
};
const decodeJwt = (jwt) => {
    if (!jwt) {
        return;
    }
    try {
        const base64Url = jwt.split('.')[1];
        const modifiedBase64Url = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const uriValue = window
            .atob(modifiedBase64Url)
            .split('')
            .map((character) => {
            const charCode = character.charCodeAt(0).toString(16);
            const hexValue = `00${charCode}`.slice(-2);
            return `%${hexValue}`;
        })
            .join('');
        const payload = decodeURIComponent(uriValue);
        return JSON.parse(payload);
    }
    catch (_err) {
        /* eslint-disable no-console */
        console.error('failed to decode jwt');
        throw _err;
    }
};
export const getAccessToken = () => {
    const auth = window === null || window === void 0 ? void 0 : window.localStorage.getItem(AUTH_TOKEN_KEY);
    // @ts-ignore
    return JSON.parse(auth).access_token;
};
const isAccessTokenExpired = () => {
    try {
        const auth = window === null || window === void 0 ? void 0 : window.localStorage.getItem(AUTH_TOKEN_KEY);
        const timeDelta = Number(window === null || window === void 0 ? void 0 : window.localStorage.getItem(AUTH_TIME_DIFF_KEY)) || 0;
        if (!auth)
            return true;
        const token = JSON.parse(auth).access_token;
        if (!token)
            return true;
        const jwt = decodeJwt(token);
        if (!jwt)
            return true;
        const { exp } = jwt;
        const expiryEpoch = (exp + timeDelta) * 1000;
        return Number.isNaN(expiryEpoch) || Date.now() > expiryEpoch;
    }
    catch (error) {
        return true;
    }
};
export const getBACNumberFromJWT = () => {
    try {
        const auth = window === null || window === void 0 ? void 0 : window.localStorage.getItem(AUTH_TOKEN_KEY);
        // @ts-ignore
        const token = JSON.parse(auth).access_token;
        const jwt = decodeJwt(token);
        if (!jwt) {
            return null;
        }
        const { bac_number: bacNumber } = jwt;
        return Array.isArray(bacNumber) ? bacNumber[0] : bacNumber;
    }
    catch (error) {
        return null;
    }
};
export const isCustomerLoggedIn = () => !isAccessTokenExpired();
//# sourceMappingURL=utils.js.map